/**
 * Left-nav sidebar (spec 0006 §5.4, refreshed).
 *
 * Renders grouped sections. The mobile presentation is a Sheet
 * triggered from the Topbar — this file owns only the visual content;
 * its container (aside vs Sheet) is decided by the parent.
 */
import { NavLink } from "react-router-dom";
import {
  ArrowDownToLine,
  Building2,
  Inbox,
  LayoutDashboard,
  ScrollText,
  Send,
  Users,
  Wallet,
  Webhook,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/use-auth";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** If true, only ``super`` role sees this item. */
  superOnly?: boolean;
}

interface NavSection {
  label: string;
  items: readonly NavItem[];
}

const SECTIONS: readonly NavSection[] = [
  {
    label: "Overview",
    items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard }],
  },
  {
    label: "Money",
    items: [
      { to: "/payment-intents", label: "Payment intents", icon: Send },
      { to: "/inbound-payments", label: "Inbound payments", icon: ArrowDownToLine },
      { to: "/balance-queries", label: "Balance queries", icon: Wallet },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/callback-inbox", label: "Callback inbox", icon: Inbox },
      { to: "/webhook-outbox", label: "Webhook outbox", icon: Webhook },
      // Audit log is SUPER-only on the backend (IsSuperAdmin); hide
      // the link rather than render a 403 for tenant operators.
      { to: "/audit-log", label: "Audit log", icon: ScrollText, superOnly: true },
    ],
  },
  {
    label: "Organization",
    // The /operators link is shown to tenant-role operators too — the
    // list page is server-filtered via ``operator_visibility_filter``
    // (only operators sharing a tenant grant are returned). Hiding the
    // link entirely would also hide it from operators who legitimately
    // need to see their teammates.
    items: [
      { to: "/tenants", label: "Tenants", icon: Building2 },
      { to: "/operators", label: "Operators", icon: Users },
    ],
  },
] as const;

export function SidebarBrand() {
  return (
    <div className="flex h-14 items-center gap-2 border-b border-white/10 px-4">
      <div
        aria-hidden
        className="flex h-7 w-7 items-center justify-center rounded-md bg-[hsl(122_48%_38%)] text-white shadow-md ring-1 ring-white/25"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 17l6-6 4 4 6-9" />
        </svg>
      </div>
      <div className="text-sm font-semibold tracking-tight">Senti</div>
      <span className="ml-1 rounded-full bg-white/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white/80">
        Admin
      </span>
    </div>
  );
}

interface SidebarNavProps {
  /** Called whenever an item is clicked — used by the mobile Sheet to auto-close. */
  onNavigate?: () => void;
}

export function SidebarNav({ onNavigate }: SidebarNavProps) {
  const { user } = useAuth();
  const isSuper = user?.role === "super";
  return (
    <nav className="flex flex-1 flex-col gap-6 overflow-y-auto px-3 py-4" aria-label="Primary">
      {SECTIONS.map((section) => {
        const items = section.items.filter((item) => !item.superOnly || isSuper);
        if (items.length === 0) return null;
        return (
          <div key={section.label} className="flex flex-col gap-1">
            <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-white/60">
              {section.label}
            </div>
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "group flex h-9 items-center gap-3 rounded-md px-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70",
                    isActive
                      ? "bg-[hsl(122_48%_33%)] text-white shadow-sm"
                      : "text-white/75 hover:bg-white/10 hover:text-white",
                  )
                }
              >
                <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                <span className="truncate">{item.label}</span>
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );
}

export function Sidebar() {
  return (
    <aside className="sidebar-surface sticky top-0 hidden h-screen w-60 shrink-0 flex-col md:flex">
      <SidebarBrand />
      <SidebarNav />
    </aside>
  );
}
